import React, { useState, useRef } from 'react';
import { Camera, Image as ImageIcon, Trash2, Eye, Plus, Zap, AlertCircle } from 'lucide-react';
import { PhotoType, WorkOrderPhoto } from '../types';
import { supabase } from '../../../lib/supabase';

interface WorkOrderPhotoUploaderProps {
  photos: WorkOrderPhoto[];
  onPhotosChange: (photos: WorkOrderPhoto[]) => void;
  readOnly?: boolean;
}

// Helper para compressão client-side com marca d'água física de segurança gravada nos pixels (Canvas API)
async function compressImageWithWatermark(
  file: File, 
  stage: PhotoType,
  maxDimension = 1400, 
  quality = 0.85
): Promise<{ base64: string; blob: Blob; timestamp: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Falha ao processar imagem no canvas'));
          return;
        }

        // 1. Desenha a foto base
        ctx.drawImage(img, 0, 0, width, height);

        // 2. Grava a marca d'água física de vistoria nos pixels da imagem
        const now = new Date();
        const timestamp = now.toISOString();
        const dateFormatted = `${now.toLocaleDateString('pt-BR')} ${now.toLocaleTimeString('pt-BR')}`;
        
        const stageLabels: Record<PhotoType, string> = {
          before: 'VISTORIA INICIAL (ANTES)',
          during: 'EXECUÇÃO TÉCNICA (DURANTE)',
          after: 'ENTREGA CONCLUÍDA (DEPOIS)',
          inspection: 'LAUDO / INSPEÇÃO'
        };

        const stageText = stageLabels[stage] || 'VISTORIA TÉCNICA';
        const bannerHeight = Math.max(36, Math.round(height * 0.05));
        const fontSize = Math.max(12, Math.round(bannerHeight * 0.38));

        // Barra escura semi-transparente no rodapé
        ctx.fillStyle = 'rgba(15, 23, 42, 0.82)';
        ctx.fillRect(0, height - bannerHeight, width, bannerHeight);

        // Linha superior de destaque (verde esmeralda)
        ctx.fillStyle = '#10b981';
        ctx.fillRect(0, height - bannerHeight, width, 2);

        // Texto do lado esquerdo (Etapa e Selo R-MAX)
        ctx.font = `900 ${fontSize}px system-ui, -apple-system, sans-serif`;
        ctx.fillStyle = '#ffffff';
        ctx.textBaseline = 'middle';
        ctx.fillText(`⚡ R-MAX • ${stageText}`, 14, height - (bannerHeight / 2));

        // Texto do lado direito (Data e Hora)
        ctx.font = `700 ${Math.max(10, fontSize - 2)}px monospace, system-ui`;
        ctx.fillStyle = '#a7f3d0';
        ctx.textAlign = 'right';
        ctx.fillText(`📅 ${dateFormatted}`, width - 14, height - (bannerHeight / 2));
        ctx.textAlign = 'left'; // Reset

        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        canvas.toBlob(blob => {
          if (blob) {
            resolve({ base64: compressedBase64, blob, timestamp });
          } else {
            resolve({ base64: compressedBase64, blob: file, timestamp });
          }
        }, 'image/jpeg', quality);
      };
      img.onerror = () => reject(new Error('Erro ao carregar imagem'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
    reader.readAsDataURL(file);
  });
}

export default function WorkOrderPhotoUploader({
  photos,
  onPhotosChange,
  readOnly = false
}: WorkOrderPhotoUploaderProps) {
  const [activeTab, setActiveTab] = useState<PhotoType>('before');
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState<WorkOrderPhoto | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);

  const filteredPhotos = photos.filter(p => p.photoType === activeTab);

  const handleFilesSelected = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const MAX_PHOTOS_PER_WO = 12;
    if (photos.length >= MAX_PHOTOS_PER_WO) {
      alert(`Limite de ${MAX_PHOTOS_PER_WO} fotos por Ordem de Serviço atingido (ideal para manter o relatório leve e rápido de carregar).`);
      return;
    }

    const availableSlots = MAX_PHOTOS_PER_WO - photos.length;
    const filesArray = Array.from(files).slice(0, availableSlots);
    if (files.length > availableSlots) {
      alert(`Apenas as primeiras ${availableSlots} foto(s) serão adicionadas para respeitar o limite de ${MAX_PHOTOS_PER_WO} fotos por OS.`);
    }

    setIsProcessing(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const currentUserId = authData?.user?.id || 'offline_user';

      const newPhotos: WorkOrderPhoto[] = [];
      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        if (!file.type.startsWith('image/')) continue;

        const { base64, blob, timestamp } = await compressImageWithWatermark(file, activeTab);
        const photoId = `photo_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
        let photoUrl = base64;

        if (currentUserId !== 'offline_user') {
          try {
            const filePath = `${currentUserId}/${photoId}.jpg`;
            const { error: uploadError } = await supabase.storage
              .from('work-order-photos')
              .upload(filePath, blob, { contentType: 'image/jpeg', upsert: true });

            if (!uploadError) {
              const { data: publicUrlData } = supabase.storage
                .from('work-order-photos')
                .getPublicUrl(filePath);
              if (publicUrlData?.publicUrl) {
                photoUrl = publicUrlData.publicUrl;
              }
            }
          } catch (storageErr) {
            console.warn('Storage indisponível, usando fallback base64 com marca dágua:', storageErr);
          }
        }

        newPhotos.push({
          id: photoId,
          workOrderId: '',
          photoType: activeTab,
          photoUrl: photoUrl,
          caption: '',
          takenAt: timestamp
        });
      }

      onPhotosChange([...photos, ...newPhotos]);
    } catch (error) {
      console.error('Erro ao comprimir fotos:', error);
      alert('Ocorreu um erro ao processar as fotos selecionadas.');
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = (photoId: string) => {
    onPhotosChange(photos.filter(p => p.id !== photoId));
  };

  const handleUpdateCaption = (photoId: string, caption: string) => {
    onPhotosChange(photos.map(p => p.id === photoId ? { ...p, caption } : p));
  };

  const tabLabels: Record<PhotoType, { label: string; desc: string }> = {
    before: { label: 'Antes (Entrada)', desc: 'Registro do estado inicial antes de começar o serviço' },
    during: { label: 'Durante (Execução)', desc: 'Fotos da montagem, fiação, estrutura e peças em andamento' },
    after: { label: 'Depois (Concluído)', desc: 'Resultado final pronto e limpo para entrega ao cliente' },
    inspection: { label: 'Vistoria / Avarias', desc: 'Comprovação de riscos, trincas ou manchas preexistentes' }
  };

  return (
    <div className="w-full bg-white border border-zinc-100 rounded-3xl p-5 md:p-6 shadow-sm space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-zinc-100 pb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-black text-brand uppercase tracking-widest bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100">
              Registro Visual
            </span>
            <span className="text-xs text-zinc-500 font-medium">
              Fotos em Boa Resolução
            </span>
          </div>
          <h3 className="text-base sm:text-lg font-black text-zinc-900">
            Fotos do Serviço (Antes / Durante / Depois)
          </h3>
        </div>

        {/* Contador Geral */}
        <div className="bg-zinc-50 border border-zinc-100 rounded-2xl px-4 py-2 flex items-center gap-2">
          <Camera className="h-4 w-4 text-brand" />
          <span className="text-xs font-bold text-zinc-700">
            {photos.length} / 12 fotos anexadas
          </span>
        </div>
      </div>

      {/* Navegação por Fases (Tabs) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {(['before', 'during', 'after', 'inspection'] as PhotoType[]).map(tabKey => {
          const isCurrent = activeTab === tabKey;
          const countInTab = photos.filter(p => p.photoType === tabKey).length;

          return (
            <button
              key={tabKey}
              type="button"
              onClick={() => setActiveTab(tabKey)}
              className={`p-3 rounded-2xl border text-left transition-all cursor-pointer active:scale-95 ${
                isCurrent
                  ? 'bg-brand text-white border-brand shadow-xs'
                  : 'bg-zinc-50 border-zinc-100 hover:border-zinc-200'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-black ${isCurrent ? 'text-white' : 'text-zinc-700'}`}>
                  {tabLabels[tabKey].label.split(' ')[0]}
                </span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  isCurrent ? 'bg-white/20 text-white' : 'bg-zinc-200 text-zinc-600'
                }`}>
                  {countInTab}
                </span>
              </div>
              <p className={`text-[10px] truncate ${isCurrent ? 'text-white/80' : 'text-zinc-400 font-medium'}`}>
                {tabLabels[tabKey].desc}
              </p>
            </button>
          );
        })}
      </div>

      {/* Botões de Ação de Upload */}
      {!readOnly && (
        <div className="flex flex-wrap gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={e => handleFilesSelected(e.target.files)}
            className="hidden"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={e => handleFilesSelected(e.target.files)}
            className="hidden"
          />

          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            disabled={isProcessing}
            className="flex-1 min-w-35 py-3 px-4 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 shadow-sm shadow-brand/20 cursor-pointer active:scale-95 disabled:opacity-50"
          >
            <Camera className="h-4 w-4" />
            <span>Tirar Foto na Câmera</span>
          </button>

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isProcessing}
            className="flex-1 min-w-35 py-3 px-4 bg-white hover:bg-zinc-50 text-zinc-700 font-bold text-xs rounded-xl border border-zinc-200 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50"
          >
            <ImageIcon className="h-4 w-4 text-zinc-500" />
            <span>Escolher da Galeria</span>
          </button>
        </div>
      )}

      {isProcessing && (
        <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center gap-3 text-xs text-brand animate-pulse">
          <Zap className="h-4 w-4 shrink-0 text-brand" />
          <span>Processando fotos com rapidez...</span>
        </div>
      )}

      {/* Galeria de Fotos da Fase Atual */}
      {filteredPhotos.length === 0 ? (
        <div className="py-10 border border-dashed border-zinc-200 rounded-2xl flex flex-col items-center justify-center text-center p-4 bg-zinc-50/50">
          <div className="w-12 h-12 rounded-2xl bg-zinc-100 flex items-center justify-center text-zinc-400 mb-3">
            <Camera className="h-6 w-6" />
          </div>
          <p className="text-xs font-bold text-zinc-700 mb-1">
            Nenhuma foto em "{tabLabels[activeTab].label}"
          </p>
          <p className="text-[11px] text-zinc-400 font-medium max-w-xs">
            {tabLabels[activeTab].desc}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3.5">
          {filteredPhotos.map(photo => (
            <div
              key={photo.id}
              className="group relative bg-white border border-zinc-100 rounded-2xl overflow-hidden flex flex-col shadow-sm"
            >
              {/* Imagem */}
              <div 
                onClick={() => setPreviewPhoto(photo)}
                className="relative h-36 w-full bg-zinc-100 overflow-hidden cursor-pointer"
              >
                <img
                  src={photo.photoUrl}
                  alt={photo.caption || 'Foto da OS'}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-neutral-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <span className="p-2 bg-white/90 rounded-full text-zinc-900 shadow-sm">
                    <Eye className="h-4 w-4" />
                  </span>
                </div>
              </div>

              {/* Legenda & Ações */}
              <div className="p-2.5 flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Legenda da foto..."
                  value={photo.caption || ''}
                  onChange={e => handleUpdateCaption(photo.id, e.target.value)}
                  disabled={readOnly}
                  className="flex-1 bg-zinc-50 border border-zinc-100 rounded-xl px-2.5 py-1.5 text-[11px] text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20 font-medium"
                />
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => handleRemovePhoto(photo.id)}
                    className="p-1.5 text-zinc-400 hover:text-red-500 rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Pré-visualização Ampliada */}
      {previewPhoto && (
        <div 
          onClick={() => setPreviewPhoto(null)}
          className="fixed inset-0 z-110 bg-neutral-950/85 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <div className="relative max-w-2xl w-full bg-white rounded-3xl overflow-hidden border border-zinc-100 shadow-2xl p-3">
            <img
              src={previewPhoto.photoUrl}
              alt="Foto ampliada"
              className="w-full max-h-[75vh] object-contain rounded-2xl"
            />
            {previewPhoto.caption && (
              <p className="p-3 text-center text-xs font-bold text-zinc-900">
                {previewPhoto.caption}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
